import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import { AttendanceStatus, EventType } from '../../types/event';
import type { FamilyEvent } from '../../types/family';
import { getMatchOutcome } from '../../types/match';
import { eventTypeAccent, eventTypeName, shortNames, timeOf } from './format';

/** Egy esemény a listában: idő, cím, helyszín, és a gyerekek visszajelzése. */
const EventRow: React.FC<{ event: FamilyEvent; showNames?: boolean }> = ({ event, showNames }) => {
  const past = new Date(event.endTime) < new Date();
  const hasScore = event.type === EventType.Match && event.homeScore != null && event.awayScore != null;
  const names = shortNames(event.myPlayers);
  const outcome = getMatchOutcome(event);
  const scoreClass =
    outcome === 'win' ? 'text-green-700' : outcome === 'loss' ? 'text-red-600' : 'text-gray-900';
  // Lezajlott eseménynél csak a tényleges jelzés érdekes; a "nem jelzett" nem teendő már
  // A meccs keretéből kimaradt játékosnál nincs mit jelezni
  const statuses = (past ? event.myPlayers.filter((p) => p.status !== AttendanceStatus.NoResponse) : event.myPlayers).filter(
    (p) => p.inSquad || p.status !== AttendanceStatus.NoResponse
  );
  // Meccsnél az ellenfél a beszédesebb, a típus és a helyszín alatta látszik
  const title = event.type === EventType.Match && event.opponent ? event.opponent : event.title;

  return (
    <Link
      to={`/events/${event.id}`}
      className={`flex items-stretch gap-3 px-4 py-3 transition-colors active:bg-gray-50 ${event.isCancelled ? 'opacity-60' : ''}`}
    >
      <div className="w-12 shrink-0 pt-0.5 text-right">
        <div className="text-[15px] font-semibold tabular-nums text-gray-900">{timeOf(event.startTime)}</div>
        <div className="text-[12px] tabular-nums text-gray-400">{timeOf(event.endTime)}</div>
      </div>
      <span className={`w-1 shrink-0 rounded-full ${eventTypeAccent[event.type]}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={`truncate text-[16px] font-medium text-gray-900 ${event.isCancelled ? 'line-through' : ''}`}>
            {title}
          </span>
          {hasScore && (
            <span className={`shrink-0 text-[15px] font-semibold tabular-nums ${scoreClass}`}>
              {event.homeScore}–{event.awayScore}
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate text-[13px] text-gray-500">
          {event.isCancelled ? (
            <>
              <span className="font-medium text-red-600">Elmarad</span>
              {event.ageGroup && ` · ${event.ageGroup}`}
            </>
          ) : (
            <>
              {eventTypeName[event.type]}
              {event.ageGroup && ` · ${event.ageGroup}`}
              {event.type === EventType.Match && event.isHomeMatch != null && (event.isHomeMatch ? ' · hazai' : ' · idegenben')}
              {event.isFutsal && ' · futsal'}
              {event.place && ` · ${event.place}`}
            </>
          )}
        </div>
      </div>
      {!event.isCancelled && event.responsesEnabled && statuses.length > 0 && (
        <div className="flex shrink-0 flex-col items-end justify-center gap-0.5">
          {statuses.map((p) => (
            <span key={p.playerId} className="flex items-center gap-1 text-[12px] text-gray-500">
              {showNames && <span className="max-w-[5.5rem] truncate">{names[p.playerId]}</span>}
              {p.status === AttendanceStatus.Attending ? (
                <CheckCircleIcon className="h-5 w-5 text-green-600" aria-label="Jön" />
              ) : p.status === AttendanceStatus.NotAttending ? (
                <XCircleIcon className="h-5 w-5 text-red-500" aria-label="Nem jön" />
              ) : (
                <QuestionMarkCircleIcon className="h-5 w-5 text-amber-500" aria-label="Még nem jelezted" />
              )}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
};

export default EventRow;
